import { useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

// Intelligent prefetching based on user behavior and navigation patterns
export const IntelligentPrefetch = () => {
  const location = useLocation();

  // Prefetch strategy based on current page
  const getPrefetchTargets = useCallback((currentPath: string): string[] => {
    const prefetchMap: Record<string, string[]> = {
      '/': ['/fabbriche', '/marchi', '/progetti', '/contatti'],
      '/fabbriche': ['/fabbriche/cioccolateria', '/fabbriche/distilleria', '/fabbriche/biscottificio'],
      '/marchi': ['/brands/la-signoria-del-cioccolato', '/brands/nux-etrusca'],
      '/progetti': ['/contatti', '/fabbriche'],
      '/contatti': ['/progetti', '/fabbriche']
    };

    return prefetchMap[currentPath] || [];
  }, []);

  // Intelligent link prefetching based on hover/focus
  const setupHoverPrefetch = useCallback(() => {
    let prefetchTimeouts: Map<string, number> = new Map();
    
    const prefetchOnHover = (href: string) => {
      // Avoid duplicate prefetches
      if (prefetchTimeouts.has(href)) return;
      
      // Delay prefetch slightly to avoid unnecessary requests
      const timeoutId = window.setTimeout(() => {
        const link = document.createElement('link');
        link.rel = 'prefetch';
        link.href = href;
        link.as = 'document';
        document.head.appendChild(link);
        
        // Also prefetch likely resources for that page
        prefetchPageResources(href);
      }, 100);
      
      prefetchTimeouts.set(href, timeoutId);
    };

    const cancelPrefetch = (href: string) => {
      const timeoutId = prefetchTimeouts.get(href);
      if (timeoutId) {
        clearTimeout(timeoutId);
        prefetchTimeouts.delete(href);
      }
    };

    // Add hover listeners to all internal links
    const addHoverListeners = () => {
      const links = document.querySelectorAll('a[href^="/"]');
      
      links.forEach((link) => {
        const href = link.getAttribute('href');
        if (!href) return;

        const handleMouseEnter = () => prefetchOnHover(href);
        const handleMouseLeave = () => cancelPrefetch(href);
        const handleFocus = () => prefetchOnHover(href);
        const handleBlur = () => cancelPrefetch(href);

        link.addEventListener('mouseenter', handleMouseEnter, { passive: true });
        link.addEventListener('mouseleave', handleMouseLeave, { passive: true });
        link.addEventListener('focus', handleFocus, { passive: true });
        link.addEventListener('blur', handleBlur, { passive: true });
        
        // Store cleanup functions
        (link as any).__prefetchCleanup = () => {
          link.removeEventListener('mouseenter', handleMouseEnter);
          link.removeEventListener('mouseleave', handleMouseLeave);
          link.removeEventListener('focus', handleFocus);
          link.removeEventListener('blur', handleBlur);
        };
      });
    };

    // Clean up existing listeners
    const cleanupHoverListeners = () => {
      const links = document.querySelectorAll('a[href^="/"]');
      links.forEach((link) => {
        if ((link as any).__prefetchCleanup) {
          (link as any).__prefetchCleanup();
        }
      });
      
      // Clear any pending timeouts
      Array.from(prefetchTimeouts.values()).forEach(clearTimeout);
      prefetchTimeouts.clear();
    };

    return { addHoverListeners, cleanupHoverListeners };
  }, []);

  // Prefetch resources likely needed for a specific page
  const prefetchPageResources = useCallback((href: string) => {
    const resourceMap: Record<string, string[]> = {
      '/fabbriche': [
        '/lovable-uploads/distilleria-hero-carousel.png'
      ],
      '/marchi': [
        '/lovable-uploads/la-signoria-logo-new.png'
      ]
    };

    const resources = resourceMap[href];
    if (resources) {
      resources.forEach(resource => {
        const link = document.createElement('link');
        link.rel = 'prefetch';
        link.href = resource;
        link.as = 'image';
        document.head.appendChild(link);
      });
    }
  }, []);

  // Preload critical resources for the current route
  const preloadCriticalResources = useCallback(() => {
    const criticalResources: Record<string, Array<{href: string, as: string, type?: string}>> = {
      '/': [
        { href: '/lovable-uploads/panorama-toscana-hero.png', as: 'image', type: 'image/png' }
      ],
      '/marchi': [
        { href: '/lovable-uploads/la-signoria-logo-new.png', as: 'image', type: 'image/png' }
      ]
    };

    const resources = criticalResources[location.pathname];
    if (resources) {
      resources.forEach(({ href, as, type }) => {
        // Check if already preloaded
        const existing = document.querySelector(`link[rel="preload"][href="${href}"]`);
        if (existing) return;

        const link = document.createElement('link');
        link.rel = 'preload';
        link.href = href;
        link.as = as;
        if (type) link.type = type;
        document.head.appendChild(link);
      });
    }
  }, [location.pathname]);

  // Intersection Observer for prefetching visible links
  const setupIntersectionPrefetch = useCallback(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const link = entry.target as HTMLAnchorElement;
          const href = link.getAttribute('href');
          
          if (href && href.startsWith('/')) {
            // Prefetch after a small delay
            setTimeout(() => {
              const prefetchLink = document.createElement('link');
              prefetchLink.rel = 'prefetch';
              prefetchLink.href = href;
              prefetchLink.as = 'document';
              document.head.appendChild(prefetchLink);
              
              // Prefetch associated resources
              prefetchPageResources(href);
            }, 1000);
            
            observer.unobserve(link);
          }
        }
      });
    }, {
      rootMargin: '200px',
      threshold: 0.1
    });

    // Observe all internal links that are not immediately visible
    // Optimized to avoid forced reflows by batching reads
    const observeLinks = () => {
      requestAnimationFrame(() => {
        const links = document.querySelectorAll('a[href^="/"]');
        const viewportHeight = window.innerHeight;
        
        // Batch all reads first to avoid layout thrashing
        const linksToObserve: Element[] = [];
        links.forEach(link => {
          const rect = link.getBoundingClientRect();
          if (rect.top > viewportHeight) {
            linksToObserve.push(link);
          }
        });
        
        // Then observe all at once
        linksToObserve.forEach(link => observer.observe(link));
      });
    };

    return { observer, observeLinks };
  }, [prefetchPageResources]);

  // Service Worker prefetch coordination
  const coordinateWithServiceWorker = useCallback(() => {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.ready.then((registration) => {
        // Send prefetch targets to service worker
        const targets = getPrefetchTargets(location.pathname);
        
        registration.active?.postMessage({
          type: 'PREFETCH_ROUTES',
          routes: targets
        });
      });
    }
  }, [location.pathname, getPrefetchTargets]);

  useEffect(() => {
    // Setup all prefetch strategies
    const { addHoverListeners, cleanupHoverListeners } = setupHoverPrefetch();
    const { observer, observeLinks } = setupIntersectionPrefetch();
    
    // Initial setup
    preloadCriticalResources();
    coordinateWithServiceWorker();
    
    // Use requestIdleCallback for non-critical prefetching
    const idleCallback = () => {
      addHoverListeners();
      observeLinks();
      
      // Prefetch likely next pages during idle time
      const targets = getPrefetchTargets(location.pathname);
      targets.forEach(target => {
        setTimeout(() => {
          const link = document.createElement('link');
          link.rel = 'prefetch';
          link.href = target;
          link.as = 'document';
          document.head.appendChild(link);
        }, 2000); // Delay to avoid impacting initial page load
      });
    };

    if ('requestIdleCallback' in window) {
      const idleId = requestIdleCallback(idleCallback, { timeout: 5000 });
      
      return () => {
        cancelIdleCallback(idleId);
        cleanupHoverListeners();
        observer.disconnect();
      };
    } else {
      // Fallback for browsers without requestIdleCallback
      const timeoutId = setTimeout(idleCallback, 3000);
      
      return () => {
        clearTimeout(timeoutId);
        cleanupHoverListeners();
        observer.disconnect();
      };
    }
  }, [location.pathname, setupHoverPrefetch, setupIntersectionPrefetch, preloadCriticalResources, coordinateWithServiceWorker, getPrefetchTargets]);

  return null;
};

export default IntelligentPrefetch;